import type { BuiltinAdapterOptions, PersistenceAdapter, FilterValues } from '../types';

/**
 * URL adapter — encodes JSON state as base64url in a named query param.
 * Plan Section "Built-in adapter factories".
 *
 * Note: we intentionally do not subscribe to history changes. Cross-tab sync is
 * out of scope for R1.
 */
export function createUrlAdapter(
  options: BuiltinAdapterOptions & { param: string },
): PersistenceAdapter {
  const { param } = options;
  return {
    load: () => {
      try {
        if (typeof window === 'undefined') return null;
        const url = new URL(window.location.href);
        const raw = url.searchParams.get(param);
        if (!raw) return null;
        const decoded = base64UrlDecode(raw);
        return JSON.parse(decoded) as FilterValues;
      } catch (err) {
        console.warn(`[FilterBar persistence] Failed to load URL param "${param}":`, err);
        return null;
      }
    },
    save: (values) => {
      try {
        if (typeof window === 'undefined') return;
        const filtered = options.exclude?.length ? omit(values, options.exclude) : values;
        const encoded = base64UrlEncode(JSON.stringify(filtered));
        const url = new URL(window.location.href);
        url.searchParams.set(param, encoded);
        window.history.replaceState({}, '', url.toString());
      } catch (err) {
        console.warn(`[FilterBar persistence] Failed to save URL param "${param}":`, err);
      }
    },
    clear: () => {
      try {
        if (typeof window === 'undefined') return;
        const url = new URL(window.location.href);
        url.searchParams.delete(param);
        window.history.replaceState({}, '', url.toString());
      } catch (err) {
        console.warn(`[FilterBar persistence] Failed to clear URL param "${param}":`, err);
      }
    },
    debounceMs: options.debounceMs ?? 0,
    exclude: options.exclude,
  };
}

function base64UrlEncode(input: string): string {
  // Browser-only; the lib is consumed in a browser context.
  const b64 = btoa(unescape(encodeURIComponent(input)));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  return decodeURIComponent(escape(atob(padded)));
}

function omit(values: FilterValues, keys: string[]): FilterValues {
  const out: FilterValues = {};
  for (const k of Object.keys(values)) {
    if (!keys.includes(k)) out[k] = values[k];
  }
  return out;
}
